import {
  ChangeDetectionStrategy,
  Component,
  inject,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { InputText } from 'primeng/inputtext';
import { Paginator } from 'primeng/paginator';
import { Select } from 'primeng/select';
import { Table, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { APP_PATHS } from '../../../core/config/app-paths';
import { TableBaseAbstractComponent } from '../../../shared/components/table-base-abstract.component';
import { PreviewDataWrapperComponent } from '../../../shared/components/preview-data-wrapper/preview-data-wrapper.component';
import { SortDirection } from '../../../shared/enums/sort-direction.enum';
import { ApplicationFormFilters } from '../../../shared/models/application-form-filters';
import { ApplicationForm } from '../../../shared/models/application-form';
import { TableResponse } from '../../../shared/models/table-response';
import { TableRequestData } from '../../../shared/models/table/table-request-param';
import { ApplicationFormsApi } from '../../../shared/services/api/application-forms.api';
import {
  APPLICATION_GROUP_LABELS,
  getApplicationGroupDropdownItems,
} from '../../../shared/enums/application-group.enum';
import { ConstLabelReaderPipe } from '../../../shared/pipes/const-label-reader.pipe';
import { PrimeFilterTarget } from '../../../shared/models/custom-types';
import { getBooleanLabel } from '../../../shared/utils/boolean.utils';

@Component({
  selector: 'app-admin-panel-applications',
  imports: [
    Button,
    Card,
    FormsModule,
    InputText,
    Paginator,
    PreviewDataWrapperComponent,
    Select,
    TableModule,
    TooltipModule,
    ConstLabelReaderPipe,
  ],
  templateUrl: './admin-panel-applications.html',
  styleUrl: './admin-panel-applications.scss',
  providers: [ApplicationFormsApi],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminPanelApplications extends TableBaseAbstractComponent<ApplicationForm> {
  @ViewChild('table') override tableRef!: Table;

  protected tableFilters: ApplicationFormFilters = {
    kod: '',
    nazwa: '',
    grupa: null,
    usuniety: null,
  };

  protected readonly areaOptions = getApplicationGroupDropdownItems();

  protected readonly deletedOptions = [
    { label: 'Tak', value: true },
    { label: 'Nie', value: false },
  ];
  protected readonly APPLICATION_GROUP_LABELS = APPLICATION_GROUP_LABELS;
  protected readonly getBooleanLabel = getBooleanLabel;
  readonly #applicationFormsApi = inject(ApplicationFormsApi);
  readonly #router = inject(Router);

  protected override apiCall$ = (
    params: TableRequestData,
  ): Observable<TableResponse<ApplicationForm>> => {
    params.sortBy = params.sortBy ?? 'kod';
    params.order = params.order ?? SortDirection.ASC;

    return this.#applicationFormsApi.getApplicationForms$(params);
  };

  override onPrimeFilterChange(target: PrimeFilterTarget, nazwa: string): void {
    super.onPrimeFilterChange(target, nazwa);
  }

  override onPrimeFilterChangeMobile(
    target: PrimeFilterTarget,
    nazwa: string,
  ): void {
    super.onPrimeFilterChangeMobile(target, nazwa);
  }

  protected resetAllFilters(): void {
    this.tableFilters = {
      kod: '',
      nazwa: '',
      grupa: null,
      usuniety: null,
    };
    this.resetFilters();
  }

  protected hasActiveFilter(field: keyof ApplicationFormFilters): boolean {
    const value = this.tableFilters[field];

    return value !== null && value !== '';
  }

  protected showDetails(applicationForm: ApplicationForm): void {
    this.#router.navigate([
      `/${APP_PATHS.admin.root}/${APP_PATHS.admin.applications}/${applicationForm.id}/${APP_PATHS.admin.details}`,
    ]);
  }
}
